import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

interface SingleUnitDetailsFormProps {
  values: {
    bedrooms?: number;
    bathrooms?: number;
    area?: number;
    rent?: number;
    amenities?: string[];
  };
  onChange: (values: {
    bedrooms?: number;
    bathrooms?: number;
    area?: number;
    rent?: number;
    amenities?: string[];
  }) => void;
}

const COMMON_AMENITIES = [
  "Air Conditioning",
  "Parking",
  "Security",
  "Generator",
  "Water Supply",
  "Furnished",
  "Kitchen",
  "Balcony",
  "Garden",
  "Swimming Pool",
  "Gym",
  "Elevator",
  "WiFi",
  "DSTV/Cable",
];

export default function SingleUnitDetailsForm({
  values,
  onChange,
}: SingleUnitDetailsFormProps) {
  const toggleAmenity = (amenity: string) => {
    const currentAmenities = values.amenities || [];
    const newAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter((a) => a !== amenity)
      : [...currentAmenities, amenity];
    onChange({ ...values, amenities: newAmenities });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          Property Details
        </h2>
        <p className="mt-2 text-base text-muted-foreground">
          Provide the specifications and features of your property
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bedrooms */}
        <div className="space-y-2">
          <Label htmlFor="bedrooms" className="text-base font-medium">
            Bedrooms <span className="text-red-500">*</span>
          </Label>
          <Input
            id="bedrooms"
            type="number"
            min="0"
            placeholder="e.g., 3"
            value={values.bedrooms || ""}
            onChange={(e) =>
              onChange({ ...values, bedrooms: parseInt(e.target.value) || 0 })
            }
            className="h-12 text-base"
            required
          />
        </div>

        {/* Bathrooms */}
        <div className="space-y-2">
          <Label htmlFor="bathrooms" className="text-base font-medium">
            Bathrooms <span className="text-red-500">*</span>
          </Label>
          <Input
            id="bathrooms"
            type="number"
            min="0"
            placeholder="e.g., 2"
            value={values.bathrooms || ""}
            onChange={(e) =>
              onChange({ ...values, bathrooms: parseInt(e.target.value) || 0 })
            }
            className="h-12 text-base"
            required
          />
        </div>

        {/* Area */}
        <div className="space-y-2">
          <Label htmlFor="area" className="text-base font-medium">
            Area (m²) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="area"
            type="number"
            min="0"
            placeholder="e.g., 120"
            value={values.area || ""}
            onChange={(e) =>
              onChange({ ...values, area: parseFloat(e.target.value) || 0 })
            }
            className="h-12 text-base"
            required
          />
          <p className="text-sm text-muted-foreground">
            Total floor area in square meters
          </p>
        </div>

        {/* Monthly Rent */}
        <div className="space-y-2">
          <Label htmlFor="rent" className="text-base font-medium">
            Monthly Rent (₦) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="rent"
            type="number"
            min="0"
            placeholder="e.g., 500000"
            value={values.rent || ""}
            onChange={(e) =>
              onChange({ ...values, rent: parseFloat(e.target.value) || 0 })
            }
            className="h-12 text-base"
            required
          />
          <p className="text-sm text-muted-foreground">
            Monthly rental amount in Nigerian Naira
          </p>
        </div>
      </div>

      {/* Amenities */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium">Amenities & Features</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Select all amenities available at this property
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {COMMON_AMENITIES.map((amenity) => (
            <div key={amenity} className="flex items-center space-x-2">
              <Checkbox
                id={amenity}
                checked={values.amenities?.includes(amenity)}
                onCheckedChange={() => toggleAmenity(amenity)}
              />
              <label
                htmlFor={amenity}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {amenity}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {(values.bedrooms || values.bathrooms || values.area || values.rent) && (
        <div className="mt-8 p-6 bg-muted/50 border rounded-xl">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">
            Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {values.bedrooms && (
              <div>
                <span className="text-sm text-muted-foreground">Bedrooms</span>
                <p className="text-lg font-semibold text-foreground">
                  {values.bedrooms}
                </p>
              </div>
            )}
            {values.bathrooms && (
              <div>
                <span className="text-sm text-muted-foreground">Bathrooms</span>
                <p className="text-lg font-semibold text-foreground">
                  {values.bathrooms}
                </p>
              </div>
            )}
            {values.area && (
              <div>
                <span className="text-sm text-muted-foreground">Area</span>
                <p className="text-lg font-semibold text-foreground">
                  {values.area} m²
                </p>
              </div>
            )}
            {values.rent && (
              <div>
                <span className="text-sm text-muted-foreground">
                  Monthly Rent
                </span>
                <p className="text-lg font-semibold text-foreground">
                  ₦{values.rent.toLocaleString()}
                </p>
              </div>
            )}
          </div>
          {values.amenities && values.amenities.length > 0 && (
            <div className="mt-4">
              <span className="text-sm text-muted-foreground">
                Selected Amenities:
              </span>
              <p className="text-sm text-foreground mt-1">
                {values.amenities.join(", ")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
